'use client';

import type { UseFormReturn } from 'react-hook-form';
import type { DriveFormValues } from '@eliadmin/core/src/types/schemas/drive-form-schema';

import { Field } from '@eliadmin/core/src/components/form/field';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export function SecondStep({
  form,
  onPrevStep,
}: {
  form: UseFormReturn<DriveFormValues>;
  onPrevStep: () => void;
}) {
  const { t } = useTranslations();

  return (
    <div className={cn('custom-section')}>
      <div className={cn('custom-container', 'mt-12 flex flex-col gap-12 md:mt-20')}>
        <div className={cn('flex items-center justify-start gap-6 pr-6 md:pr-0')}>
          <HtmlRendererRaw
            as="h2"
            className={cn(
              'text-3xl font-normal tracking-tighter sm:text-4xl lg:text-5xl xl:text-6xl',
            )}
            html={`<span class="text-primary font-light">02</span> ${t('text360')}`}
          />
          <div className={cn('h-[2px] w-10 bg-black sm:w-12 md:w-16 lg:w-20')} />
        </div>
        <div className="flex w-full items-center justify-center">
          <div className="grid w-full max-w-[817px] grid-cols-2 gap-4 sm:gap-6">
            <Field
              control={form.control}
              name={'name'}
              required
              placeholder={t('text1044')}
              className={{
                wrapper: 'col-span-2 w-full',
              }}
            />
            <Field
              control={form.control}
              name={'email'}
              required
              type="email"
              placeholder={`${t('text35')}*`}
              className={{
                wrapper: 'col-span-2 w-full sm:col-span-1',
              }}
            />
            <Field
              control={form.control}
              name={'phone'}
              placeholder={`${t('text1258')}`}
              className={{
                wrapper: 'col-span-2 w-full sm:col-span-1',
              }}
            />
            <FormField
              control={form.control}
              name={'text'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn('col-span-2', 'ea-contact-form__field ea-contact-form__text-field')}
                >
                  <FormControl>
                    <div
                      className={cn(
                        'flex w-full flex-row items-center space-x-2',
                        'ea-contact-form__input-wrapper ea-contact-form__text-input-wrapper',
                      )}
                    >
                      <Textarea placeholder={t('text615') + ' *'} {...(field as any)} rows={5} />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'gdpr'}
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'col-span-2 flex items-center',
                    'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
                  )}
                >
                  <FormControl>
                    <Checkbox
                      ref={field.ref}
                      name={field.name}
                      disabled={field.disabled}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      'text-sm font-light tracking-tight sm:text-base lg:text-lg',
                      'ea-contact-form__gdpr-checkbox-label',
                    )}
                  >
                    {t('text613')}{' '}
                    <Link
                      prefetch={false}
                      href={'/ochrana-osobnych-udajov'}
                      className="text-primary underline"
                    >
                      {t('text614')}
                    </Link>
                  </FormLabel>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'g-recaptcha-response'}
              render={() => (
                <FormItem required className={cn('col-span-2')}>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex w-full flex-col-reverse items-center justify-center gap-2 sm:flex-row sm:justify-between">
          <Button
            type="button"
            onClick={onPrevStep}
            className="flex h-12 w-full items-center justify-center gap-3 bg-[#F5F5F5] pl-5 pr-6 text-sm font-medium text-black sm:w-fit md:text-base lg:h-14"
          >
            <MdKeyboardArrowLeft size={20} /> {t('text2028')}
          </Button>
          <Button
            type="submit"
            className="flex h-12 w-full items-center justify-center gap-3 bg-primary pl-6 pr-5 text-sm font-medium text-black sm:w-fit md:text-base lg:h-14"
          >
            {t('text1027')}
          </Button>
        </div>
      </div>
    </div>
  );
}
